import clsx from 'clsx';
import React, { useMemo } from 'react';

import { transformNodeIdToPath } from '@srcDS/js/utils/helpers';
import { useMenuContext } from '@srcDS/provider/menu/MenuContext';
import { Image } from '../../../../atoms/image/Image';
import { ResponsiveImage } from '../../../../atoms/image/ResponsiveImage';
import styles from './NavigationBanner.module.scss';
import { NavigationTile } from './NavigationBanner.props';
import Button from '@srcDS/components/atoms/button';

type NavigationBannerProps = {
  tile: NavigationTile;
  setShownMenu?: (value: boolean) => void;
};

const getCorrectImageUrl = (imageObj) => {
  if (typeof window !== 'undefined') {
    const screenWidth = window.innerWidth;
    if (screenWidth <= 768 && (imageObj?.mobile || imageObj?.mobileCloudinary)) {
      return imageObj?.mobileCloudinary?.[0] || imageObj?.mobile;
    } else if (screenWidth <= 1024 && (imageObj?.tablet || imageObj?.tabletCloudinary)) {
      return imageObj?.tabletCloudinary?.[0] || imageObj?.tablet;
    } else {
      return imageObj?.desktopCloudinary?.[0] || imageObj?.desktop;
    }
  }
};

export const NavigationBanner: React.FC<NavigationBannerProps> = React.memo(({ tile, setShownMenu }) => {
  const { closeMobileMenuOnClick } = useMenuContext();

  const {
    promoBackgroundColor = null,
    promoBackgroundImage = null,
    promoConditions,
    promoText = null,
    promoTitle,
    promoLinks,
  } = tile;

  const hasPromo = !!(promoBackgroundColor || promoBackgroundImage || promoText);
  const promoLink = promoLinks?.length > 0 ? promoLinks[0] : null;

  if (!hasPromo) return null;

  const stylesElement = {
    backgroundColor: promoBackgroundColor ? promoBackgroundColor : 'none',
    height: promoBackgroundImage ? 'auto' : 420,
  };
  const hasImage = !!getCorrectImageUrl(promoBackgroundImage);

  const closeMenu = () => {
    closeMobileMenuOnClick && closeMobileMenuOnClick(null);
    setShownMenu && setShownMenu(false);
  };

  const LinkNode = ({ promoLink }) => {
    let href = '';
    if (promoLink?.buttonUrl?.openInNewWindow) {
      href =
        promoLink?.buttonUrl?.type === 'page-folder'
          ? transformNodeIdToPath(promoLink?.buttonUrl?.pageFolder?.pageFolderId)
          : promoLink?.buttonUrl?.pageFolder?._url;
    } else {
      href = promoLink?.buttonUrl?.pageFolder?._url || promoLink?.buttonUrl?.link || '';
    }

    const { buttonTitle, buttonIcon, iconPosition, buttonType, buttonState, buttonUrl } = promoLink;

    return (
      // @ts-ignore
      <Button
        onClick={closeMenu}
        iconPosition={iconPosition}
        icon={buttonIcon}
        external={buttonUrl?.openInNewWindow}
        href={href}
        className={styles.promoLink}
        variant={buttonType}
        ariaLabel={promoText ?? ''}
        state={buttonState}
      >
        {buttonTitle}
      </Button>
    );
  };

  const renderMedia = useMemo(() => {
    if (hasImage) {
      const currentMedia = getCorrectImageUrl(promoBackgroundImage);
      return (
        <Image
          style={{ aspectRatio: currentMedia?.aspectRatio?.replace(':', '/') || 'initial' }}
          alt={currentMedia?.title}
          className={styles.image}
          image={
            currentMedia?.resource_type
              ? { ...currentMedia, url: currentMedia.secure_url || currentMedia.url }
              : currentMedia.url
          }
          opts={{
            width: 400,
            height: 420,
          }}
          remote={undefined}
        />
      );
    }

    return (
      <ResponsiveImage
        source={{ ...promoBackgroundImage, width: 400, height: 420 }}
        loading="lazy"
        className={styles.bgImage}
        mobileSource={undefined}
      />
    );
  }, [promoBackgroundImage]);

  return (
    <div className={styles.menuBanner}>
      {(promoBackgroundImage || hasImage || (!hasImage && promoBackgroundColor)) && (
        <span
          className={clsx(
            styles.imageHolder,
            !promoBackgroundImage?.media && !hasImage ? styles.imageHolderWithBg : '',
          )}
          style={stylesElement}
        >
          {renderMedia}
        </span>
      )}
      <span className={styles.bannerContent}>
        {promoTitle && <span className={styles.promoBannerTitle}>{promoTitle}</span>}
        {promoText && <span className={styles.promoBannerText}>{promoText}</span>}
        {promoConditions && <small className={styles.promoBannerSmall}>{promoConditions}</small>}
        {!!promoLink && <LinkNode promoLink={promoLink} />}
      </span>
    </div>
  );
});
